import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface RecallQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface RecallKpiData {
  completedRecall: number
  completedRecallGoal: number
  overduePatients: string
  scheduledRecall: string
  recallRate: string
  recallReactivation: string
  recallProduction: string
  comparisonLabel: string
  completedDelta: string
  completedTrend: 'up' | 'down'
  overdueDelta: string
  overdueTrend: 'up' | 'down'
  productionDelta: string
  productionTrend: 'up' | 'down'
}

export interface RecallByProviderRow {
  id: string
  provider: string
  completed: number
  overdue: number
  scheduled: number
  recallRate: string
  recallProduction: string
  reactivationRate: string
}

export interface RecallOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: RecallKpiData
  byProvider: RecallByProviderRow[]
}

export interface RecallChartPoint {
  label: string
  completed: number
  scheduled: number
  overdue: number
}

export interface RecallChartConfig {
  data: RecallChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

export interface RecallOverduePatientRow {
  id: string
  patientName: string
  lastRecall: string
  daysOverdue: number
  recallType: string
  provider: string
  status: string
}

export interface RecallDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: RecallKpiData
  chart: RecallChartConfig
  byProvider: RecallByProviderRow[]
  overduePatients: RecallOverduePatientRow[]
}

const recallGet = <T>(
  path: string,
  params: RecallQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/recall/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getRecallOverview = (
  params: RecallQueryParams,
  options?: { signal?: AbortSignal },
): Promise<RecallOverviewResult> => recallGet('overview', params, options)

export const getRecallDetail = (
  params: RecallQueryParams,
  options?: { signal?: AbortSignal },
): Promise<RecallDetailResult> => recallGet('detail', params, options)
